/**
 * A minimal promise wrapper over IndexedDB.
 *
 * Hand-rolled rather than pulled from a library because the surface this app
 * actually needs is get/getAll/put/delete/bulkPut plus one honest multi-store
 * transaction. A dependency would buy nothing and would put a third party
 * between the offline write path and the disk, which is the one place in this
 * app where a bug is unrecoverable data loss.
 *
 * Layout: one object store per syncable entity keyed by `id`, plus `outbox`
 * (auto-incrementing key so ops drain in strict FIFO order), `conflict` and
 * `dead` (sidelined ops, keyed by `op_id`), and `meta` (out-of-line keys; holds
 * the sync cursor and the backup timestamp).
 */

import { SYNCABLE, type Entity } from "../../shared/types";

const DB_NAME = "punchboard";
/**
 * Bump when the store layout changes; `upgrade` is written to be idempotent.
 *
 * **Adding an entity to `SYNCABLE` is a layout change**, and this is the only
 * place that says so. `upgrade` builds the stores from `SYNCABLE`, but the
 * browser only calls it when the version it holds is lower than this number — so
 * a new entity with the version left alone gets its store on a fresh install and
 * never on an existing one, and every read of it throws `NotFoundError` on
 * exactly the devices that have been using the app. 2 is `overall_rating`.
 */
const DB_VERSION = 2;

export const OUTBOX_STORE = "outbox";
export const CONFLICT_STORE = "conflict";
export const DEAD_STORE = "dead";
export const META_STORE = "meta";

export type StoreName =
  | Entity
  | typeof OUTBOX_STORE
  | typeof CONFLICT_STORE
  | typeof DEAD_STORE
  | typeof META_STORE;

export type Mode = "readonly" | "readwrite";

/**
 * The `keyPath` every store is created with, `null` meaning out-of-line keys.
 *
 * This is the single source of truth: `upgrade` below builds the real stores
 * from it, and `test/support/fake-idb.ts` keys its double from it. Those two
 * used to state the layout independently, and the day they disagreed — the
 * double keying `conflict` and `dead` by `id` where the real one keys them by
 * `op_id` — cost a CI suite that was red for a day, because a delete by `op_id`
 * removes nothing while `getAll` goes on returning the row. Add a store here and
 * both sides learn about it at once; there is no second list to forget.
 */
export const KEY_PATHS: Readonly<Record<string, string | null>> = {
  ...Object.fromEntries(SYNCABLE.map((entity) => [entity, "id"])),
  [OUTBOX_STORE]: "seq",
  [CONFLICT_STORE]: "op_id",
  [DEAD_STORE]: "op_id",
  [META_STORE]: null,
};

/** IndexedDB is absent in Bun's test runtime and in some private-mode browsers. */
export function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function upgrade(db: IDBDatabase): void {
  for (const entity of SYNCABLE) {
    if (db.objectStoreNames.contains(entity)) continue;
    const store = db.createObjectStore(entity, { keyPath: KEY_PATHS[entity] });
    // Pull writes rows above a cursor; the index makes "what changed" cheap.
    store.createIndex("updated_seq", "updated_seq");
  }
  if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
    // Auto-increment key rather than op_id: two ops created in the same
    // millisecond must still drain in the order they were written.
    const store = db.createObjectStore(OUTBOX_STORE, {
      keyPath: KEY_PATHS[OUTBOX_STORE],
      autoIncrement: true,
    });
    store.createIndex("op_id", "op_id", { unique: true });
  }
  for (const name of [CONFLICT_STORE, DEAD_STORE]) {
    if (!db.objectStoreNames.contains(name)) {
      db.createObjectStore(name, { keyPath: KEY_PATHS[name] });
    }
  }
  // `meta` is the one out-of-line store — `KEY_PATHS` says `null`, and
  // `createObjectStore` wants the option absent rather than null.
  if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (!idbAvailable()) return Promise.reject(new Error("IndexedDB unavailable"));
  if (dbPromise) return dbPromise;
  const attempt = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => upgrade(req.result);
    req.onblocked = () => reject(new Error("IndexedDB upgrade blocked by another tab"));
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onsuccess = () => {
      const db = req.result;
      // Another tab opening a newer version must not be blocked forever; drop
      // this handle and let the next call re-open.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
  });
  dbPromise = attempt;
  // A failed open must not become the permanent answer. The slot is cached, so
  // without this one rejection — a private-mode browser refusing storage, a
  // half-applied upgrade, a test that stubbed `indexedDB` with an object that
  // has no `open` — is inherited by every later caller for the life of the page,
  // and no retry can ever get past it.
  //
  // The price is worth stating plainly, because it is not one extra open: while
  // the failure lasts, every `transaction()` — so every get, put and count —
  // finds an empty slot and issues a fresh open of its own. That is the trade
  // taken deliberately. A browser that refuses storage for good pays for a retry
  // it will never win, and one that refuses for a moment recovers by itself.
  void attempt.catch(() => {
    if (dbPromise === attempt) dbPromise = null;
  });
  return attempt;
}

/**
 * Drop the cached handle so the next call opens the database again.
 *
 * The handle is one module-level promise, which is right in a browser — one tab,
 * one database, opened once — and is a trap in the test runner, where every file
 * shares the process. Whichever file opens first decides which `indexedDB` the
 * *whole run* talks to, and every later file's own fake is then installed on a
 * global nobody reads. That is not a hypothetical: it cost a CI suite that was
 * red for a day, because the file that won on Linux keyed `dead` and `conflict`
 * by `id` where the real layout keys them by `op_id`, so a delete by `op_id`
 * silently removed nothing while every read still returned the row.
 *
 * Tests call this when installing their double; see `test/support/fake-idb.ts`.
 */
export function closeDb(): void {
  const open = dbPromise;
  dbPromise = null;
  // Swallowed on purpose: a handle that never opened has nothing to close, and
  // failing to close one is not worth failing a caller that is discarding it.
  void open?.then((db) => db.close()).catch(() => undefined);
}

/** Wrapped object store. Every method returns a promise tied to one request. */
export interface TxStore {
  get<T>(key: IDBValidKey): Promise<T | undefined>;
  getAll<T>(query?: IDBKeyRange | IDBValidKey | null, count?: number): Promise<T[]>;
  getAllKeys(query?: IDBKeyRange | IDBValidKey | null, count?: number): Promise<IDBValidKey[]>;
  put<T>(value: T, key?: IDBValidKey): Promise<IDBValidKey>;
  add<T>(value: T): Promise<IDBValidKey>;
  delete(key: IDBValidKey | IDBKeyRange): Promise<void>;
  count(query?: IDBKeyRange | IDBValidKey): Promise<number>;
  clear(): Promise<void>;
  indexGetAll<T>(index: string, query?: IDBKeyRange | IDBValidKey | null): Promise<T[]>;
}

function wrap(store: IDBObjectStore): TxStore {
  return {
    get: (key) => request(store.get(key)),
    getAll: (query, count) => request(store.getAll(query ?? null, count)),
    getAllKeys: (query, count) => request(store.getAllKeys(query ?? null, count)),
    put: (value, key) => request(store.put(value as unknown, key)),
    add: (value) => request(store.add(value as unknown)),
    delete: (key) => request(store.delete(key)).then(() => undefined),
    count: (query) => request(store.count(query)),
    clear: () => request(store.clear()).then(() => undefined),
    indexGetAll: (index, query) => request(store.index(index).getAll(query ?? null)),
  };
}

export interface TxScope {
  store(name: StoreName): TxStore;
}

/**
 * Run `fn` inside one IndexedDB transaction and resolve only once that
 * transaction has actually committed. Callers get real atomicity: if `fn`
 * throws, the transaction aborts and nothing it wrote survives.
 */
export async function transaction<T>(
  names: StoreName[],
  mode: Mode,
  fn: (scope: TxScope) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  const tx = db.transaction(names, mode);
  const settled = new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
  });
  const cache = new Map<string, TxStore>();
  const scope: TxScope = {
    store(name) {
      const hit = cache.get(name);
      if (hit) return hit;
      const made = wrap(tx.objectStore(name));
      cache.set(name, made);
      return made;
    },
  };
  let result: T;
  try {
    result = await fn(scope);
  } catch (err) {
    try {
      tx.abort();
    } catch {
      // Already finished; the settled promise carries the real outcome.
    }
    throw err;
  }
  await settled;
  return result;
}

export function get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return transaction([store], "readonly", (s) => s.store(store).get<T>(key));
}

export function getAll<T>(
  store: StoreName,
  query?: IDBKeyRange | IDBValidKey | null,
  count?: number,
): Promise<T[]> {
  return transaction([store], "readonly", (s) => s.store(store).getAll<T>(query, count));
}

export function put<T>(store: StoreName, value: T, key?: IDBValidKey): Promise<void> {
  return transaction([store], "readwrite", async (s) => {
    await s.store(store).put(value, key);
  });
}

export function del(store: StoreName, key: IDBValidKey): Promise<void> {
  return transaction([store], "readwrite", (s) => s.store(store).delete(key));
}

export function bulkPut<T>(store: StoreName, values: readonly T[]): Promise<void> {
  return transaction([store], "readwrite", async (s) => {
    const handle = s.store(store);
    for (const value of values) await handle.put(value);
  });
}

export function count(store: StoreName): Promise<number> {
  return transaction([store], "readonly", (s) => s.store(store).count());
}

export function getMeta<T>(key: string): Promise<T | undefined> {
  return get<T>(META_STORE, key);
}

export function setMeta<T>(key: string, value: T): Promise<void> {
  return put(META_STORE, value, key);
}

/** Every store this app owns. Used by export/import and by wipe-on-signout. */
export const ALL_STORES: StoreName[] = [
  ...SYNCABLE,
  OUTBOX_STORE,
  CONFLICT_STORE,
  DEAD_STORE,
  META_STORE,
];
